import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Eye, X } from 'lucide-react';
import { useProxy } from '../contexts/ProxyContext';
import { usePermissions } from '../hooks/usePermissions';

export const ProxyBanner: React.FC = () => {
  const { isProxyActive, effectiveRole, availableRoles, setProxyRole } = useProxy();
  const { hasRole } = usePermissions();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Only show banner for admin/super users when proxy is active
  const canUseProxy = hasRole('admin') || hasRole('super');

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Early return after all hooks have been called
  if (!canUseProxy || !isProxyActive) {
    return null;
  }

  const handleRoleChange = (roleId: string) => {
    setProxyRole(roleId);
    setIsDropdownOpen(false);
  };

  const handleExitProxy = () => {
    setProxyRole(null);
    setIsDropdownOpen(false);
  };

  const currentRole = availableRoles.find(role => role.id === effectiveRole);

  // Define colors for each user type
  const getRoleColor = (roleId: string) => {
    switch (roleId) {
      case 'client':
        return 'from-purple-500 to-purple-600';
      case 'user':
        return 'from-green-500 to-green-600';
      case 'manager':
        return 'from-orange-500 to-orange-600';
      case 'admin':
        return 'from-red-500 to-red-600';
      case 'super':
        return 'from-indigo-500 to-indigo-600';
      default:
        return 'from-gray-500 to-gray-600';
    }
  };

  const getRoleDisplayName = (roleId: string) => {
    switch (roleId) {
      case 'client':
        return 'Client User';
      case 'user':
        return 'Standard User';
      case 'manager':
        return 'Manager';
      case 'admin':
        return 'Administrator';
      case 'super':
        return 'Super Admin';
      default:
        return roleId;
    }
  };

  return (
    <div className={`fixed top-0 left-0 right-0 z-50 h-[25px] bg-gradient-to-r ${getRoleColor(effectiveRole)} text-white text-xs font-medium flex items-center justify-end px-4 shadow-sm`}>
      {/* Controls */}
      <div className="flex items-center gap-2">
        {/* Role Dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="flex items-center gap-1 px-2 py-0.5 bg-white/20 hover:bg-white/30 rounded text-xs transition-colors"
          >
            <Eye className="h-3 w-3" />
            {getRoleDisplayName(effectiveRole)}
            <ChevronDown className={`h-3 w-3 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
          </button>
          
          {isDropdownOpen && (
            <div className="absolute top-full right-0 mt-1 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-600 py-1 min-w-48 z-50">
              {availableRoles
                .filter(role => role.id !== 'super') // Remove super user from proxy options
                .map((role) => (
                <button
                  key={role.id}
                  onClick={() => handleRoleChange(role.id)}
                  className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                    role.id === effectiveRole ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'text-gray-700 dark:text-gray-300'
                  }`}
                >
                  <div className="font-medium">{getRoleDisplayName(role.id)}</div>
                  <div className="text-gray-500 dark:text-gray-400 text-xs">{role.description}</div>
                </button>
              ))}
            </div>
          )}
        </div>
        
        {/* Exit Proxy Button */}
        <button
          onClick={handleExitProxy}
          className="flex items-center gap-1 px-2 py-0.5 bg-red-500/20 hover:bg-red-500/30 rounded text-xs transition-colors"
          title="Exit proxy mode"
        >
          <X className="h-3 w-3" />
          Exit
        </button>
      </div>
    </div>
  );
};
